import { useMutation, useQueryClient } from 'react-query';
import { getPaymentMethodUseCases } from '@subscriptions/composition';

/**
 * Hook for forcing (retrying) the payment of a subscription block whose last
 * charge failed. Wraps the PaymentMethod `forcePayment` use case (B1).
 *
 * On success it invalidates the block detail query so the whole detail reloads
 * with the fresh payment status.
 *
 * @returns Mutation result for forcing a block payment
 *
 * @example
 * const { mutateAsync: forcePayment, isLoading } = useForcePayment();
 * await forcePayment(blockUuid);
 */
export const useForcePayment = () => {
  const queryClient = useQueryClient();
  const { forcePayment } = getPaymentMethodUseCases();

  return useMutation<void, Error, string>((blockUuid) => forcePayment.execute(blockUuid), {
    onSuccess: (_data, blockUuid) => {
      queryClient.invalidateQueries(['subscriptions', 'blocks', 'detail', blockUuid]);
    },
  });
};
